import { useEffect, useState, useMemo } from "react";
import { adminListFestivals, adminCreateFestival, adminUpdateFestival, adminDeleteFestival } from "@/lib/api";
import { CalendarDays, Plus, Trash2, Pencil, X, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

const inp = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";
const EMPTY = { date: "", name: "", deva: "", type: "festival", significance: "", significance_hi: "", link: "", hidden: false };

export default function AdminFestivals() {
  const [items, setItems] = useState(null);
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => adminListFestivals().then((d) => { setItems(d.festivals || []); setTypes(d.types || []); }).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => { setForm(EMPTY); setEditId(null); };

  const save = async () => {
    if (!form.date || !form.name.trim()) { toast.error("Date and name are required"); return; }
    setSaving(true);
    try {
      editId ? await adminUpdateFestival(editId, form) : await adminCreateFestival(form);
      toast(editId ? "Festival updated" : "Festival added", { description: "Run Sync (Preview → Live) to publish." });
      reset(); load();
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };
  const edit = (f) => { setForm({ ...EMPTY, ...f }); setEditId(f.id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const remove = async (id) => { if (!window.confirm("Delete this entry?")) return; try { await adminDeleteFestival(id); load(); } catch { toast.error("Delete failed"); } };

  const shown = useMemo(() => (items || []).filter((f) => !q || (f.name + f.deva + f.type).toLowerCase().includes(q.toLowerCase())), [items, q]);

  return (
    <div className="mx-auto max-w-3xl" data-testid="admin-festivals">
      <div className="mb-2 flex items-center gap-2.5"><CalendarDays className="h-6 w-6 text-saffron" /><h1 className="font-serif-latin text-3xl text-ink">Festival Calendar</h1></div>
      <p className="mb-6 text-sm text-ink-3">Add and edit the festivals, observances & celebrations shown on the <b>/calendar</b> grid. Set a <b>link</b> (e.g. a festival landing page <code>/festival/navratri</code> or a pooja-vidhi <code>/rituals/...</code>) so the calendar card opens to full details.</p>

      <div className="mb-8 border border-line bg-surface p-5" data-testid="festival-composer">
        <div className="mb-3 flex items-center justify-between">
          <p className="overline-label text-gold">{editId ? "Edit entry" : "New entry"}</p>
          {editId && <button onClick={reset} className="inline-flex items-center gap-1 text-xs text-ink-3 hover:text-ink"><X className="h-3.5 w-3.5" /> Cancel</button>}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-xs text-ink-3">Date<input type="date" className={inp} value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="festival-date" /></label>
          <label className="text-xs text-ink-3">Type
            <select className={inp} value={form.type} onChange={(e) => set("type", e.target.value)} data-testid="festival-type">
              {types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="text-xs text-ink-3">Name (English)<input className={inp} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="festival-name" /></label>
          <label className="text-xs text-ink-3">नाम (देवनागरी)<input className={`${inp} font-deva`} value={form.deva} onChange={(e) => set("deva", e.target.value)} /></label>
          <label className="text-xs text-ink-3 sm:col-span-2">Link (opens from the calendar card)<input className={inp} value={form.link} onChange={(e) => set("link", e.target.value)} placeholder="/festival/navratri  or  /rituals/ganesh-puja-vidhi" data-testid="festival-link" /></label>
          <label className="text-xs text-ink-3 sm:col-span-2">Significance (English)<textarea rows={2} className={inp} value={form.significance} onChange={(e) => set("significance", e.target.value)} /></label>
          <label className="text-xs text-ink-3 sm:col-span-2">महत्व (हिन्दी)<textarea rows={2} className={`${inp} font-deva`} value={form.significance_hi} onChange={(e) => set("significance_hi", e.target.value)} /></label>
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm text-ink-2"><input type="checkbox" checked={form.hidden} onChange={(e) => set("hidden", e.target.checked)} /> Hidden</label>
        <button onClick={save} disabled={saving} data-testid="festival-save" className="mt-4 inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-2.5 text-sm font-medium text-white hover:bg-[var(--saffron-dark)] disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {editId ? "Save entry" : "Add entry"}
        </button>
      </div>

      <div className="mb-3 flex items-center gap-2 border border-line bg-surface px-3 py-2">
        <Search className="h-4 w-4 text-ink-3" />
        <input className="w-full bg-transparent text-sm text-ink focus:outline-none" placeholder="Search festivals…" value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="shrink-0 text-xs text-ink-3">{shown.length}</span>
      </div>
      {items === null ? <p className="py-10 text-center text-ink-3">…</p> : (
        <ul className="space-y-2">
          {shown.map((f) => (
            <li key={f.id} className={`flex items-center justify-between gap-3 border border-line bg-surface p-3 ${f.hidden ? "opacity-60" : ""}`} data-testid={`festival-row-${f.id}`}>
              <div className="min-w-0">
                <p className="font-serif-latin text-base text-ink">{f.name} {f.deva ? <span className="font-deva text-ink-2">· {f.deva}</span> : null}</p>
                <p className="flex flex-wrap items-center gap-2 text-xs text-ink-3">
                  <span>{f.date}</span>
                  <span className="rounded-full bg-saffron/10 px-2 py-0.5 text-saffron">{f.type}</span>
                  {f.link && <span className="truncate text-gold">→ {f.link}</span>}
                </p>
              </div>
              <div className="flex shrink-0 gap-1">
                <button onClick={() => edit(f)} className="p-2 text-ink-3 hover:text-saffron"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(f.id)} className="p-2 text-ink-3 hover:text-[var(--saffron-dark)]"><Trash2 className="h-4 w-4" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
